import { Injectable } from '@angular/core'
import { AircraftLimitApiResponse, CoverageApiResponse, CoverageLimitApiResponse, SubCoverageApiResponse } from '../../models/coverages/coverage-api.model'
import {
  AircraftLimit,
  AlbertAmountWithCurrency,
  AviationHullApiResponse,
  AviationLiabilities,
  BaseSubCoverage,
  CommercialGeneralLiability,
  ReinsuranceOrder
} from '../../models/coverages/base-coverage.model'
import { AlbertValueObject } from '../../models/member/member.model'
import {
  AVIATION_HULL_PANELS,
  AVIATION_LIABILITIES_PANELS,
  COMMERCIAL_GENERAL_LIABILITY_PANELS,
  SubCoveragePanel
} from '../../models/coverages/coverage-panels.model'
import { INSURED_SECTIONS } from '../../models/insured-sections.model'
import { NavSection } from '@shared/services/detailview-navigation.service'
import { hasActualValue } from '@core/utils/form-utils'
import { isCompleteAircraftLimit } from '@features/insured/utils/aircraft-limit.util'

// Spares Engine Test Run has no panel of its own but is still saved.
const HULL_SUBCOVERAGE_KEYS = [...AVIATION_HULL_PANELS.map(panel => panel.key), 'sparesEngineTestRun' as const]
const LIABILITY_SUBCOVERAGE_KEYS = AVIATION_LIABILITIES_PANELS.map(panel => panel.key)
const CGL_SUBCOVERAGE_KEYS = COMMERCIAL_GENERAL_LIABILITY_PANELS.map(panel => panel.key)

/**
 * Maps coverages between the backend's API shape and the form models of the insured detailview,
 * and builds the detailview's section navigation.
 */
@Injectable({ providedIn: 'root' })
export class CoverageMapperService {

  /**
   * Sub-coverage types whose backend enum cannot be derived from the form property name
   * (e.g. trailing acronyms dropped from the form key to keep identifiers short / avoid false-positive
   * secret detection in our pipeline).
   */
  private static readonly SUBCOVERAGE_TYPE_OVERRIDES: Readonly<Record<string, string>> = {
    GENERAL_THIRD_PARTY_LIABILITY_NON_AVIATION_PRIMARY: 'generalThirdPartyLiabilityNonAviationPrimary',
    AVIATION_MOTOR_VEHICLE_LIABILITY_REGISTERED_EXCESS_ONLY: 'aviationMotorVehicleLiabilityRegistered',
    AVIATION_MOTOR_VEHICLE_LIABILITY_NON_REGISTERED: 'aviationMotorVehicleLiabilityUnregistered'
  }

  /**
   * Maps the Aviation Liabilities coverage of a policy to the liabilities form model. Sub-coverages
   * missing from the response get their defaults.
   *
   * @param apiCoverages - All coverages of the policy as returned by the backend
   * @returns Aviation Liabilities form model
   */
  public mapToAviationLiabilities(apiCoverages: CoverageApiResponse[]): AviationLiabilities {
    const liabCoverages = apiCoverages.filter(c => c.coverageType === 'AVIATION_LIABILITIES')
    const firstCov = liabCoverages[0]
    const firstSub = firstCov?.subcoverages[0]

    const result: AviationLiabilities = {
      general: {
        uuid: firstCov?.uuid ?? null,
        externalId: firstCov?.externalId ?? null,
        periodFrom: firstSub ? this.toDateValueObject(firstSub.effectiveDate) : this.toValueObject(null),
        periodTo: firstSub ? this.toDateValueObject(firstSub.expiryDate) : this.toValueObject(null)
      },
      aviationCombinedThirdPartyAndPassengerLiability: this.getDefaultSubCoverage(true, true, true, false, false),
      aviationThirdPartyLiability: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationPassengerLiability: this.getDefaultSubCoverage(true, false, true, false, false),
      aviationFreightLiability: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationProductsLiability: this.getDefaultSubCoverage(true, true, false, true, false),
      aviationHangarkeepersLiability: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationLandingGroundLiability: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationMotorVehicleLiabilityRegistered: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationMotorVehicleLiabilityUnregistered: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationExtendedGeneralLiabilityAvn59: this.getDefaultSubCoverage(true, false, false, false, false),
      aviationThirdPartyWarLiability: this.getDefaultSubCoverage(true, true, false, false, false),
      aviationThirdPartyWarExcessLiability: {
        ...this.getDefaultSubCoverage(false, false, false, false, false),
        aircraftLimits: [],
        origamiAircraftLimits: [],
        aircraftLimitConsistency: null
      },
      nonAviationExcessLiability: this.getDefaultSubCoverage(true, false, false, false, false)
    }

    liabCoverages.forEach(cov => {
      cov.subcoverages.forEach(sub => {
        const propName = this.mapSubCoverageTypeToProperty(sub.subCoverageType) as keyof AviationLiabilities
        if (propName && propName !== 'general' && propName in result) {
          const subCoverage = this.mapSubCoverage(sub)
          this.mapSpecificSublimits(sub, subCoverage)

          if (propName === 'aviationThirdPartyWarExcessLiability') {
            result.aviationThirdPartyWarExcessLiability = {
              ...result.aviationThirdPartyWarExcessLiability,
              ...subCoverage,
              ...this.mapAircraftLimits(sub)
            }
          } else {
            ;(result as unknown as Record<string, unknown>)[propName] = subCoverage
          }
        }
      })
    })

    return result
  }

  /**
   * Maps the Aviation Hull coverage of a policy to the hull form model. Sub-coverages missing from
   * the response get their defaults; the Origami side of Spares Engine Test Run is derived from
   * whether that sub-coverage has any Origami value.
   *
   * @param apiCoverages - All coverages of the policy as returned by the backend
   * @returns Aviation Hull form model
   */
  public mapToAviationHull(apiCoverages: CoverageApiResponse[]): AviationHullApiResponse {
    const hullCoverages = apiCoverages.filter(c => c.coverageType === 'AVIATION_HULL')
    const firstHull = hullCoverages[0]
    const firstSub = firstHull?.subcoverages[0]

    const result: AviationHullApiResponse = {
      general: {
        uuid: firstHull?.uuid ?? null,
        externalId: firstHull?.externalId ?? null,
        periodFrom: firstSub ? this.toDateValueObject(firstSub.effectiveDate) : this.toValueObject(null),
        periodTo: firstSub ? this.toDateValueObject(firstSub.expiryDate) : this.toValueObject(null),
        sparesEngineTestRun: firstHull?.sparesEngineTestRun ?? this.toValueObject(null),
        sparesJointlyFor: firstHull?.sparesJointlyFor ?? this.toValueObject(false),
        sparesTwinInsured: firstHull?.sparesTwinInsured ?? this.toValueObject(null),
        hullDeductibleJointlyFor: firstHull?.hullDeductibleJointlyFor ?? this.toValueObject(false),
        hullDeductibleTwinInsured: firstHull?.hullDeductibleTwinInsured ?? this.toValueObject(null)
      },
      hullAllRisk: this.getDefaultSubCoverage(false, false, false, false, false),
      hullWarAndAlliedPerils: this.getDefaultSubCoverage(true, true, false, false, true),
      sparesLocation: this.getDefaultSubCoverage(true, false, false, false, false, true),
      sparesLocationExcessOnly: this.getDefaultSubCoverage(true, false, false, false, false),
      sparesTransport: this.getDefaultSubCoverage(true, false, false, false, false, true),
      sparesTransportExcessOnly: this.getDefaultSubCoverage(true, false, false, false, false),
      sparesWar: this.getDefaultSubCoverage(true, false, false, false, false),
      sparesEngineTestRun: this.getDefaultSubCoverage(false, false, false, false, false),
      hullDeductible: this.getDefaultSubCoverage(false, true, false, false, false)
    }

    hullCoverages.forEach(cov => {
      cov.subcoverages.forEach(sub => {
        const propName = this.mapSubCoverageTypeToProperty(sub.subCoverageType) as keyof AviationHullApiResponse
        if (propName && propName !== 'general' && propName in result) {
          const subCoverage = this.mapSubCoverage(sub)
          this.mapSpecificSublimits(sub, subCoverage)
          ;(result as unknown as Record<string, unknown>)[propName] = subCoverage
        }
      })
    })

    // Origami has no flag of its own for this: the subcoverage having any Origami data is the flag.
    result.general.sparesEngineTestRun = {
      ...result.general.sparesEngineTestRun,
      externalValue: this.hasAnyExternalValue(result.sparesEngineTestRun)
    }

    return result
  }

  /**
   * Builds the section navigation of the insured detailview from `INSURED_SECTIONS`: one entry per
   * section followed by one per sub-coverage panel.
   *
   * @returns Nav sections in display order, `mainIndex` being the section's position
   */
  public getNavSections(): NavSection[] {
    return INSURED_SECTIONS.flatMap((section, mainIndex) => [
      { label: section.label, mainIndex },
      ...this.toSubNavSections(section.label, mainIndex, section.subPanels)
    ])
  }

  /**
   * Maps the hull form back to the API shape for saving. Sub-coverages without any entered value are
   * left out, Spares Engine Test Run included even though it has no panel.
   *
   * @param form - Raw value of the Aviation Hull form
   * @param policyId - Policy the coverage belongs to
   * @returns Aviation Hull coverage to save
   */
  public mapAviationHullFormToCoverage(form: AviationHullApiResponse, policyId: string): CoverageApiResponse {
    const subcoverages = HULL_SUBCOVERAGE_KEYS
      .map(key => this.subCoverageFormToApi(form[key] as BaseSubCoverage, key as string))
      .filter((sub): sub is SubCoverageApiResponse => sub !== null)

    return {
      uuid: form.general.uuid ?? '',
      policyId,
      externalId: form.general.externalId,
      coverageType: 'AVIATION_HULL',
      subcoverages,
      sparesEngineTestRun: form.general.sparesEngineTestRun,
      sparesJointlyFor: form.general.sparesJointlyFor ?? undefined,
      sparesTwinInsured: form.general.sparesTwinInsured,
      hullDeductibleJointlyFor: form.general.hullDeductibleJointlyFor ?? undefined,
      hullDeductibleTwinInsured: form.general.hullDeductibleTwinInsured
    }
  }

  /**
   * Maps the liabilities form back to the API shape for saving. Sub-coverages without any entered
   * value are left out.
   *
   * @param form - Raw value of the Aviation Liabilities form
   * @param policyId - Policy the coverage belongs to
   * @returns Aviation Liabilities coverage to save
   */
  public mapAviationLiabilitiesFormToCoverage(form: AviationLiabilities, policyId: string): CoverageApiResponse {
    const subcoverages = LIABILITY_SUBCOVERAGE_KEYS
      .map(key => this.subCoverageFormToApi(form[key] as BaseSubCoverage, key as string))
      .filter((sub): sub is SubCoverageApiResponse => sub !== null)

    return {
      uuid: form.general.uuid ?? '',
      policyId,
      externalId: form.general.externalId,
      coverageType: 'AVIATION_LIABILITIES',
      subcoverages
    }
  }

  /**
   * Maps the Commercial General Liability coverage of a policy to the CGL form model. Sub-coverages
   * missing from the response get their defaults.
   *
   * @param apiCoverages - All coverages of the policy as returned by the backend
   * @returns Commercial General Liability form model
   */
  public mapToCommercialGeneralLiability(apiCoverages: CoverageApiResponse[]): CommercialGeneralLiability {
    const cglCoverages = apiCoverages.filter(c => c.coverageType === 'COMMERCIAL_GENERAL_LIABILITY')
    const firstCov = cglCoverages[0]

    const result: CommercialGeneralLiability = {
      general: {
        uuid: firstCov?.uuid ?? null,
        externalId: firstCov?.externalId ?? null
      },
      generalThirdPartyLiabilityNonAviationPrimary: this.getDefaultSubCoverage(true, false, false, false, false)
    }

    cglCoverages.forEach(cov => {
      cov.subcoverages.forEach(sub => {
        const propName = this.mapSubCoverageTypeToProperty(sub.subCoverageType) as keyof CommercialGeneralLiability
        if (propName && propName !== 'general' && propName in result) {
          const subCoverage = this.mapSubCoverage(sub)
          this.mapSpecificSublimits(sub, subCoverage)
          ;(result as unknown as Record<string, unknown>)[propName] = subCoverage
        }
      })
    })

    return result
  }

  /**
   * Maps the CGL form back to the API shape for saving. Sub-coverages without any entered value are
   * left out.
   *
   * @param form - Raw value of the Commercial General Liability form
   * @param policyId - Policy the coverage belongs to
   * @returns Commercial General Liability coverage to save
   */
  public mapCommercialGeneralLiabilityFormToCoverage(form: CommercialGeneralLiability, policyId: string): CoverageApiResponse {
    const subcoverages = CGL_SUBCOVERAGE_KEYS
      .map(key => this.subCoverageFormToApi(form[key] as BaseSubCoverage, key as string))
      .filter((sub): sub is SubCoverageApiResponse => sub !== null)

    return {
      uuid: form.general.uuid ?? '',
      policyId,
      externalId: form.general.externalId,
      coverageType: 'COMMERCIAL_GENERAL_LIABILITY',
      subcoverages
    }
  }

  /** Whether any value object nested in `value` (arrays and objects included) has an Origami value. */
  private hasAnyExternalValue(value: unknown): boolean {
    if (Array.isArray(value)) return value.some(item => this.hasAnyExternalValue(item))
    if (typeof value !== 'object' || value === null) return false
    if ('externalValue' in value) return hasActualValue(value.externalValue)
    return Object.values(value).some(item => this.hasAnyExternalValue(item))
  }

  /** One nav section per panel, labelled "<coverage> - <panel>" and pointing at the panel by its key. */
  private toSubNavSections(coverageLabel: string, mainIndex: number, panels: readonly SubCoveragePanel[]): NavSection[] {
    return panels.map(panel => ({
      label: `${coverageLabel} - ${panel.label}`,
      mainIndex,
      subValue: panel.key
    }))
  }

  private mapSubCoverage(api: SubCoverageApiResponse): BaseSubCoverage {
    return {
      uuid: api.uuid,
      externalId: api.externalId,
      periodFrom: this.toDateValueObject(api.effectiveDate),
      periodTo: this.toDateValueObject(api.expiryDate),
      policyNumber: api.number,
      limit: this.findLimit(api.subcoverageLimits, 'LIMIT') ?? this.getDefaultAmount(),
      aggregateLimit: this.findLimit(api.subcoverageLimits, 'AGGREGATE') ?? this.getDefaultAmount(),
      reinsurance: this.mapReinsurance(api),
      personalInjurySublimit: this.getDefaultAmount(),
      groundingLiabilitySublimit: this.getDefaultAmount(),
      confiscationSublimit: this.getDefaultAmount(),
      sparesDeductible: this.getDefaultAmount(),
      policyCoverageType: api.policyCoverageType
    }
  }

  private mapReinsurance(api: SubCoverageApiResponse): ReinsuranceOrder {
    const additional = api.reinsuranceOrderAdditional ?? null
    return {
      share: api.reinsuranceShare,
      // Records created before the backend gained this field come back without it.
      frontingCompany: api.reinsuranceFrontingCompany ?? this.toValueObject(null),
      additionalInfoContent: this.toValueObject(additional)
    }
  }

  private findLimit(limits: CoverageLimitApiResponse[], type: string): AlbertAmountWithCurrency | undefined {
    const limit = limits.find(l => l.subcoverageLimitType === type)
    if (!limit) return undefined

    return {
      uuid: limit.uuid,
      value: limit.subcoverageLimitValue,
      currency: limit.subcoverageLimitCurrency
    }
  }

  private mapSpecificSublimits(api: SubCoverageApiResponse, target: BaseSubCoverage): void {
    target.personalInjurySublimit = this.findLimit(api.subcoverageLimits, 'PERSONAL_INJURY_SUBLIMIT') ?? this.getDefaultAmount()
    target.groundingLiabilitySublimit = this.findLimit(api.subcoverageLimits, 'GROUNDING_LIABILITY_SUBLIMIT') ?? this.getDefaultAmount()
    target.confiscationSublimit = this.findLimit(api.subcoverageLimits, 'CONFISCATION') ?? this.getDefaultAmount()
    target.sparesDeductible = this.findLimit(api.subcoverageLimits, 'SPARES_DEDUCTIBLE') ?? this.getDefaultAmount()
  }

  private mapSubCoverageTypeToProperty(type: string): string {
    const override = (CoverageMapperService.SUBCOVERAGE_TYPE_OVERRIDES as Record<string, string>)[type]
    if (hasActualValue(override)) {
      return override
    }
    // Convert AVIATION_COMBINED_THIRD_PARTY_AND_PASSENGER_LIABILITY to aviationCombinedThirdPartyAndPassengerLiability
    return type.toLowerCase().replaceAll(/_([a-z])/g, (g) => g[1]!.toUpperCase())
  }

  // ---------- Form -> API mapping ----------

  private toDateValueObject(value: string | AlbertValueObject<string>): AlbertValueObject<Date | null> {
    if (typeof value === 'object' && value !== null && 'localValue' in value) {
      const local = value.localValue ? new Date(value.localValue) : null
      const external = value.externalValue ? new Date(value.externalValue) : null
      return {
        localValue: local && !Number.isNaN(local.getTime()) ? local : null,
        externalValue: external && !Number.isNaN(external.getTime()) ? external : null,
        changed: value.changed
      }
    }

    const date = value ? new Date(value) : null
    const finalDate = date && !Number.isNaN(date.getTime()) ? date : null
    return {
      localValue: finalDate,
      externalValue: finalDate,
      changed: false
    }
  }

  private toValueObject<T>(value: T): AlbertValueObject<T> {
    return {
      localValue: value,
      externalValue: value,
      changed: false
    }
  }

  private getDefaultAmount(): AlbertAmountWithCurrency {
    return {
      uuid: null,
      value: this.toValueObject(null),
      currency: this.toValueObject(null)
    }
  }

  private getDefaultSubCoverage(
    limit = true,
    aggregateLimit = true,
    personalInjurySublimit = true,
    groundingLiabilitySublimit = true,
    confiscationSublimit = true,
    sparesDeductible = false
  ): BaseSubCoverage {

    const subCoverage: BaseSubCoverage = {
      uuid: null,
      externalId: null,
      periodFrom: this.toValueObject(null),
      periodTo: this.toValueObject(null),
      policyNumber: this.toValueObject(''),
      reinsurance: {
        share: this.toValueObject(null),
        frontingCompany: this.toValueObject(''),
        additionalInfoContent: this.toValueObject('')
      },
      policyCoverageType: this.toValueObject(null)
    }
    if (limit) subCoverage.limit = this.getDefaultAmount()
    if (aggregateLimit) subCoverage.aggregateLimit = this.getDefaultAmount()
    if (personalInjurySublimit) subCoverage.personalInjurySublimit = this.getDefaultAmount()
    if (groundingLiabilitySublimit) subCoverage.groundingLiabilitySublimit = this.getDefaultAmount()
    if (confiscationSublimit) subCoverage.confiscationSublimit = this.getDefaultAmount()
    if (sparesDeductible) subCoverage.sparesDeductible = this.getDefaultAmount()

    return subCoverage
  }

  /**
   * Builds a subcoverage payload from the form snapshot, or returns null when the
   * subcoverage has neither an existing uuid nor any user-provided local values
   * (i.e. nothing worth persisting).
   */
  private subCoverageFormToApi(
    sub: BaseSubCoverage,
    propertyName: string,
  ): SubCoverageApiResponse | null {
    if (!hasActualValue(sub.uuid) && !this.hasAnyLocalValue(sub)) {
      return null
    }

    const subCoverageType = this.propertyNameToSubCoverageType(propertyName)
    const limits = this.collectLimits(sub, sub.uuid)
    const aircraftLimits = this.aircraftLimitsOf(sub)

    return {
      uuid: sub.uuid ?? '',
      coverageUuid: '',
      externalId: sub.externalId,
      subCoverageType,
      number: sub.policyNumber as AlbertValueObject<string>,
      effectiveDate: this.toDateApiValueObject(sub.periodFrom),
      expiryDate: this.toDateApiValueObject(sub.periodTo),
      policyCoverageType: sub.policyCoverageType,
      reinsuranceShare: sub.reinsurance.share,
      reinsuranceFrontingCompany: sub.reinsurance.frontingCompany,
      reinsuranceOrderAdditional: sub.reinsurance.additionalInfoContent.localValue,
      subcoverageLimits: limits,
      // Only the war excess liability has them; incomplete entries stay on screen until they can be stored.
      ...(aircraftLimits && {
        aircraftLimits: aircraftLimits.filter(isCompleteAircraftLimit).map(entry => this.toAircraftLimitApi(entry))
      })
    }
  }

  /** Both lists arrive sorted by the backend: aircraft type, then limit. */
  private mapAircraftLimits(
    sub: SubCoverageApiResponse
  ): Pick<AviationLiabilities['aviationThirdPartyWarExcessLiability'], 'aircraftLimits' | 'origamiAircraftLimits' | 'aircraftLimitConsistency'> {
    return {
      aircraftLimits: (sub.aircraftLimits ?? []).map(entry => this.toAircraftLimit(entry)),
      origamiAircraftLimits: (sub.origamiAircraftLimits ?? []).map(entry => this.toAircraftLimit(entry)),
      aircraftLimitConsistency: sub.aircraftLimitConsistency ?? null
    }
  }

  private toAircraftLimit(entry: AircraftLimitApiResponse): AircraftLimit {
    return {
      uuid: entry.uuid ?? null,
      aircraftType: entry.aircraftType ?? null,
      limit: { value: entry.limitValue ?? null, currency: entry.limitCurrency ?? null }
    }
  }

  private toAircraftLimitApi(entry: AircraftLimit): AircraftLimitApiResponse {
    return {
      uuid: hasActualValue(entry.uuid) ? entry.uuid : null,
      aircraftType: hasActualValue(entry.aircraftType) ? entry.aircraftType : null,
      limitValue: entry.limit.value,
      limitCurrency: entry.limit.currency
    }
  }

  private aircraftLimitsOf(sub: BaseSubCoverage): AircraftLimit[] | undefined {
    return 'aircraftLimits' in sub ? (sub as { aircraftLimits: AircraftLimit[] }).aircraftLimits : undefined
  }

  private collectLimits(sub: BaseSubCoverage, subCoverageUuid: string | null): CoverageLimitApiResponse[] {
    const candidates: { amount: AlbertAmountWithCurrency | undefined; type: string }[] = [
      { amount: sub.limit, type: 'LIMIT' },
      { amount: sub.aggregateLimit, type: 'AGGREGATE' },
      { amount: sub.personalInjurySublimit, type: 'PERSONAL_INJURY_SUBLIMIT' },
      { amount: sub.groundingLiabilitySublimit, type: 'GROUNDING_LIABILITY_SUBLIMIT' },
      { amount: sub.confiscationSublimit, type: 'CONFISCATION' },
      { amount: sub.sparesDeductible, type: 'SPARES_DEDUCTIBLE' }
    ]

    return candidates
      .filter((c): c is { amount: AlbertAmountWithCurrency; type: string } => {
        if (c.amount === undefined) {
          return false
        }
        return hasActualValue(c.amount.uuid) || this.hasAmountValue(c.amount)
      })
      .map(c => ({
        uuid: c.amount.uuid ?? '',
        subCoverageUuid: subCoverageUuid ?? '',
        subcoverageLimitType: c.type,
        subcoverageLimitValue: c.amount.value,
        subcoverageLimitCurrency: c.amount.currency
      }))
  }

  private hasAmountValue(amount: AlbertAmountWithCurrency): boolean {
    return hasActualValue(amount.value.localValue) || hasActualValue(amount.currency.localValue)
  }

  private hasAnyLocalValue(sub: BaseSubCoverage): boolean {
    return hasActualValue(sub.externalId)
      || hasActualValue(sub.periodFrom.localValue)
      || hasActualValue(sub.periodTo.localValue)
      || hasActualValue(sub.policyNumber.localValue)
      || (!!sub.limit && this.hasAmountValue(sub.limit))
      || (!!sub.aggregateLimit && this.hasAmountValue(sub.aggregateLimit))
      || (!!sub.personalInjurySublimit && this.hasAmountValue(sub.personalInjurySublimit))
      || (!!sub.groundingLiabilitySublimit && this.hasAmountValue(sub.groundingLiabilitySublimit))
      || (!!sub.confiscationSublimit && this.hasAmountValue(sub.confiscationSublimit))
      || (!!sub.sparesDeductible && this.hasAmountValue(sub.sparesDeductible))
      || hasActualValue(sub.reinsurance.share.localValue)
      || hasActualValue(sub.reinsurance.frontingCompany.localValue)
      || hasActualValue(sub.reinsurance.additionalInfoContent.localValue)
      || (this.aircraftLimitsOf(sub) ?? []).some(isCompleteAircraftLimit)
  }

  private propertyNameToSubCoverageType(prop: string): string {
    const overrideEntry = Object.entries(CoverageMapperService.SUBCOVERAGE_TYPE_OVERRIDES)
      .find(([, propName]) => propName === prop)
    if (overrideEntry) return overrideEntry[0]
    // Convert aviationCombinedThirdPartyAndPassengerLiability to AVIATION_COMBINED_THIRD_PARTY_AND_PASSENGER_LIABILITY
    return prop.replaceAll(/([A-Z])/g, '_$1').toUpperCase()
  }

  private toDateApiValueObject(vo: AlbertValueObject<Date | null>): AlbertValueObject<string> {
    return {
      localValue: vo.localValue ? vo.localValue.toISOString() : '',
      externalValue: vo.externalValue ? vo.externalValue.toISOString() : '',
      changed: vo.changed
    }
  }
}
